/* eslint-disable tailwindcss/no-arbitrary-value */
import { useScrollLock } from 'hooks/useScrollLock';
import { useLayoutEffect, useRef } from 'react';
import { DayPicker } from 'react-day-picker';
import { useSheetDismissal } from '../hooks/useSheetDismissal';
import type { RangeDayPickerProps, SheetSummaryState } from '../types';
import Button from '$/components/Button';
import Close from '$/components/icons/Close';
import Info from '$/components/icons/Info';

// Stable keys for the weekday bar — the labels themselves are not unique in
// every locale's abbreviation set.
const weekdayKeys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

type DateRangeSheetProps = {
  dayPickerProps: RangeDayPickerProps;
  weekdayNames: readonly string[];
  summary: SheetSummaryState;
  isRangeSelected: boolean;
  onClose: () => void;
  onConfirmClick: () => void;
  confirmLabel: string;
  isConfirmDisabled: boolean;
  guidanceText?: string;
  unavailableReasonText?: string;
  title: string;
  closeLabel: string;
  /** Month to bring into view when the sheet opens, as an index into the span. */
  scrollToMonthIndex: number;
};

/**
 * Full-screen mobile date sheet (CMS-1854): stacked months that scroll under a
 * fixed weekday bar, with the selection summary and confirm pinned to the
 * bottom. Rules, click cycle and confirm gating are the desktop ones — only the
 * presentation differs.
 */
export function DateRangeSheet({
  dayPickerProps,
  weekdayNames,
  summary,
  isRangeSelected,
  onClose,
  onConfirmClick,
  confirmLabel,
  isConfirmDisabled,
  guidanceText,
  unavailableReasonText,
  title,
  closeLabel,
  scrollToMonthIndex,
}: DateRangeSheetProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const weekdayBarRef = useRef<HTMLDivElement>(null);

  useScrollLock(true);
  useSheetDismissal({ containerRef, onClose });

  // Open on the month holding the committed start rather than at the top.
  // rdp exposes no per-month ref, but renders `.rdp-month` nodes in span order.
  // Runs before paint so the sheet never shows the top month first and jumps.
  useLayoutEffect(() => {
    const scroll = scrollRef.current;

    if (scrollToMonthIndex <= 0 || !scroll) {
      return;
    }

    const month = scroll.querySelectorAll('.rdp-month')[scrollToMonthIndex];

    if (!(month instanceof HTMLElement)) {
      return;
    }

    // Measure through viewport rects rather than offsetTop: the offset parent
    // of a month is rdp's own `position: relative` wrapper, not the scroll box.
    // The weekday bar is sticky inside the same box, so its height has to come
    // off the target or the month caption opens underneath it.
    const barHeight = weekdayBarRef.current?.offsetHeight ?? 0;
    const delta =
      month.getBoundingClientRect().top - scroll.getBoundingClientRect().top;

    scroll.scrollTop += delta - barHeight;
    // Runs once per mount: the span is frozen while the sheet is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      aria-label={title}
      aria-modal
      className="date-range-picker date-range-picker--sheet fixed inset-0 z-[--z-index-modal] flex h-dvh flex-col bg-fill/card focus:outline-none"
      data-component="date-range-sheet"
      ref={containerRef}
      role="dialog"
      // A click on non-focusable content (title, guidance, summary text) lands
      // on the dialog instead of blurring to `body`, keeping focus inside.
      tabIndex={-1}
    >
      <header className="relative flex items-center justify-center border-b border-borders/default p-spacing/padding/y-sm pt-[calc(env(safe-area-inset-top)+var(--spacing-padding-y-sm))]">
        <button
          aria-label={closeLabel}
          className="absolute left-spacing/padding/x flex size-5 items-center justify-center text-text/primary"
          onClick={onClose}
          type="button"
        >
          <Close className="size-full" />
        </button>
        <span className="font-semibold text-text/primary text-font-size/subheading-1 leading-line-height/subheading-1">
          {title}
        </span>
      </header>

      {guidanceText && (
        <div
          aria-live="polite"
          className="mx-[--spacing-padding-x] mt-[--spacing-padding-y-sm] rounded-[8px] px-3 py-2 font-semibold bg-fill/primary-light text-text/primary text-[0.9rem]"
        >
          {guidanceText}
        </div>
      )}

      <div
        className="flex-1 overflow-y-auto overscroll-contain px-[--spacing-padding-x]"
        ref={scrollRef}
      >
        {/* Sticky *inside* the scroll container: sharing one content box is what
            keeps the columns aligned with the day grid at every width, with or
            without a visible scrollbar. */}
        <div
          aria-hidden
          className="sticky top-0 z-10 grid grid-cols-7 border-b border-borders/default py-spacing/gap/soft-8px bg-fill/card"
          ref={weekdayBarRef}
        >
          {weekdayNames.map((name, index) => (
            <span
              className="text-center font-semibold text-text/secondary-lighter text-[0.78rem]"
              key={weekdayKeys[index] ?? name}
            >
              {name}
            </span>
          ))}
        </div>

        <div className={isRangeSelected ? 'range-selected' : undefined}>
          <DayPicker {...dayPickerProps} />
        </div>
      </div>

      <footer className="border-t border-t-borders/default px-[--spacing-padding-x] py-spacing/padding/y-sm pb-[calc(env(safe-area-inset-bottom)+var(--spacing-padding-y-sm))] bg-fill/card">
        {unavailableReasonText && (
          <div
            aria-live="polite"
            className="mb-spacing/gap/soft-8px flex items-center gap-2 rounded-[8px] border border-fill/primary px-3 py-2 bg-fill/primary-light text-text/link text-[0.85rem]"
          >
            <Info aria-hidden className="size-4 shrink-0" />
            <span>{unavailableReasonText}</span>
          </div>
        )}

        <div className="flex items-start justify-between gap-spacing/gap/x-sm">
          <div className="min-w-0">
            <span className="block text-text/secondary-lighter text-[0.72rem]">
              {summary.startLabel}
            </span>
            <strong className="font-semibold text-text/primary text-[1.05rem]">
              {summary.startValue}
            </strong>
          </div>
          <div className="min-w-0 text-right">
            <span className="block text-text/secondary-lighter text-[0.72rem]">
              {summary.endLabel}
            </span>
            <strong className="font-semibold text-text/link text-[1.05rem]">
              {summary.endValue}
            </strong>
          </div>
        </div>

        <Button
          className="mt-spacing/gap/soft-12px w-full"
          disabled={isConfirmDisabled}
          onClick={onConfirmClick}
          styleType="primary"
          type="button"
        >
          {confirmLabel}
        </Button>
      </footer>
    </div>
  );
}
